import { useSyncExternalStore } from "react";

/**
 * AtlasPlate — a tenant-neutral L30 "survey plate" brand device.
 *
 * The customer portal has a memorable Transition33 welcome built on a
 * cartographic latitude/meridian motif (`portal-web/.../pakistan-map.tsx`).
 * That asset is intentionally Government-of-Pakistan-specific: a real
 * country outline, the 33°N gold rule through Islamabad, named cities.
 *
 * The staff app has no equivalent brand moment — sign-in and empty
 * states are generic. This component brings the *craft* of that motif
 * (a graticule survey plate, a bearing/compass mark, registration
 * crosshairs, mono coordinate annotations) to the staff surfaces
 * WITHOUT any tenant-specific geography. It is pure abstract
 * cartography:
 *   - No country outline, no city names, no partner wordmark.
 *   - The highlighted motif is the **30° belt** — the shaded band between
 *     30°N and 30°S, the brand's namesake ("Latitude30" / L30) read as the
 *     zone L30 works across, NOT a single 30°N line at one tenant's
 *     territory. House identity, not any one partner's geography.
 *   - All colour comes from semantic design tokens (`var(--color-*)`);
 *     no raw hex. The SVG strokes use `currentColor` + token vars so a
 *     dark/branded theme recolours the plate for free.
 *
 * Decorative only: the whole thing is `aria-hidden` and pointer-events
 * are left to the caller's layout, so it never interferes with the
 * sign-in form or empty-state content or their a11y tree.
 *
 * File ownership: net-new component for platform-assessment X7
 * (Stream 4 — "an L30 AtlasPlate for portal welcome, staff sign-in,
 * empty tenants"). Colours are token-based only.
 *
 * @see docs/assessments/2026-07-05-platform-assessment.md Stream 4, X7
 */

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function reducedMotionSnapshot(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/**
 * `prefers-reduced-motion` observer. The global CSS baseline neutralises
 * CSS animations, but the SMIL draw on the plate is a separate animation
 * surface — so we read the media query directly and render the plate in
 * its final (fully-drawn) state when the user has opted out of motion.
 */
function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, reducedMotionSnapshot, () => false);
}

interface AtlasPlateProps {
  /**
   * Render the mono coordinate annotations (PLATE / LAT / PROJ meta
   * strip + corner tags). Turn off when the plate is used as a faint
   * backdrop behind other content. Default `true`.
   */
  annotations?: boolean;
  /**
   * Subtly draw the parallel / meridian / compass ring on mount.
   * Automatically disabled under `prefers-reduced-motion`, in which
   * case the plate renders fully-drawn with no `<animate>` elements.
   * Default `true`.
   */
  animate?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

// "Latitude 30" is the belt BETWEEN 30°N and 30°S — the transition-economy
// zone L30 works across — NOT a single 30° line. So the plate highlights BOTH
// parallels and shades the band between them; the compass sits mid-belt on the
// meridian. Fractions are shared by the full-bleed line layer and the
// non-distorted compass overlay so the compass sits on the crossing at any
// aspect ratio.
const MERIDIAN_X = 0.62;
const PARALLEL_N_Y = 0.3; // 30°N — northern edge of the belt
const PARALLEL_S_Y = 0.6; // 30°S — southern edge of the belt
const BELT_CENTER_Y = (PARALLEL_N_Y + PARALLEL_S_Y) / 2;

export function AtlasPlate({
  annotations = true,
  animate = true,
  className,
  style,
}: AtlasPlateProps) {
  const reduced = usePrefersReducedMotion();
  const draw = animate && !reduced;
  // stroke-dashoffset seed: 1 = undrawn (animates to 0), 0 = final.
  const seed = draw ? 1 : 0;

  return (
    <div
      aria-hidden="true"
      className={className}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        color: "var(--color-brand)",
        backgroundColor: "var(--color-surface-muted)",
        ...style,
      }}
    >
      {/* ── Layer 1: full-bleed graticule + highlighted parallel/meridian.
          preserveAspectRatio="none" lets the square grid stretch to fill
          the panel like a real plotted sheet. ── */}
      {/* eslint-disable-next-line l30/no-inline-svg-icon -- decorative brand art, not iconography (canon 08-design/iconography.md exception) */}
      <svg
        style={{ position: "absolute", inset: 0 }}
        width="100%"
        height="100%"
        viewBox="0 0 1000 1000"
        preserveAspectRatio="none"
        role="presentation"
      >
        <defs>
          <pattern id="atlas-grid-minor" width={25} height={25} patternUnits="userSpaceOnUse">
            <path
              d="M 25 0 L 0 0 0 25"
              fill="none"
              stroke="currentColor"
              strokeWidth={0.5}
              opacity={0.08}
            />
          </pattern>
          <pattern id="atlas-grid-major" width={125} height={125} patternUnits="userSpaceOnUse">
            <path
              d="M 125 0 L 0 0 0 125"
              fill="none"
              stroke="currentColor"
              strokeWidth={0.75}
              opacity={0.18}
            />
          </pattern>
        </defs>
        <rect width="1000" height="1000" fill="url(#atlas-grid-minor)" />
        <rect width="1000" height="1000" fill="url(#atlas-grid-major)" />

        {/* The 30° belt — the shaded zone between 30°N and 30°S (L30's territory). */}
        <rect
          x={0}
          y={PARALLEL_N_Y * 1000}
          width={1000}
          height={(PARALLEL_S_Y - PARALLEL_N_Y) * 1000}
          fill="var(--color-accent)"
          opacity={0.06}
        />

        {/* Fainter meridian (vertical) — drawn top-to-bottom. */}
        <line
          x1={MERIDIAN_X * 1000}
          x2={MERIDIAN_X * 1000}
          y1={0}
          y2={1000}
          stroke="var(--color-accent)"
          strokeWidth={1}
          opacity={0.35}
          pathLength={1}
          strokeDasharray={1}
          strokeDashoffset={seed}
        >
          {draw && (
            <animate
              attributeName="stroke-dashoffset"
              from={1}
              to={0}
              dur="0.9s"
              begin="0.15s"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.4 0 0.2 1"
            />
          )}
        </line>

        {/* The two bounding parallels — 30°N and 30°S — drawn left-to-right.
            Together with the shaded band they read as "the 30° belt", the
            brand's namesake, rather than a single line at 30°N. */}
        {[PARALLEL_N_Y, PARALLEL_S_Y].map((y, i) => (
          <line
            key={y}
            x1={0}
            x2={1000}
            y1={y * 1000}
            y2={y * 1000}
            stroke="var(--color-accent)"
            strokeWidth={1.5}
            pathLength={1}
            strokeDasharray={1}
            strokeDashoffset={seed}
          >
            {draw && (
              <animate
                attributeName="stroke-dashoffset"
                from={1}
                to={0}
                dur="0.9s"
                begin={`${i * 0.12}s`}
                fill="freeze"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.4 0 0.2 1"
              />
            )}
          </line>
        ))}
      </svg>

      {/* ── Layer 2: compass / bearing mark. Its own non-distorted SVG,
          centred on the parallel×meridian crossing, so the ring stays
          circular regardless of the panel's aspect ratio. ── */}
      <div
        style={{
          position: "absolute",
          left: `${MERIDIAN_X * 100}%`,
          top: `${BELT_CENTER_Y * 100}%`,
          transform: "translate(-50%, -50%)",
          width: "clamp(96px, 22vw, 200px)",
          aspectRatio: "1 / 1",
        }}
      >
        {/* eslint-disable-next-line l30/no-inline-svg-icon -- decorative brand art, not iconography (canon 08-design/iconography.md exception) */}
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          role="presentation"
        >
          {/* Outer ring — drawn as a stroked circle. */}
          <circle
            cx={50}
            cy={50}
            r={38}
            fill="none"
            stroke="currentColor"
            strokeWidth={0.75}
            opacity={0.5}
            pathLength={1}
            strokeDasharray={1}
            strokeDashoffset={seed}
          >
            {draw && (
              <animate
                attributeName="stroke-dashoffset"
                from={1}
                to={0}
                dur="0.7s"
                begin="0.5s"
                fill="freeze"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.4 0 0.2 1"
              />
            )}
          </circle>
          {/* Inner tick ring. */}
          <circle
            cx={50}
            cy={50}
            r={30}
            fill="none"
            stroke="currentColor"
            strokeWidth={0.5}
            opacity={0.25}
          />

          {/* Cardinal ticks. */}
          <g stroke="currentColor" strokeWidth={0.75} opacity={0.6}>
            <line x1={50} y1={4} x2={50} y2={14} />
            <line x1={50} y1={86} x2={50} y2={96} />
            <line x1={4} y1={50} x2={14} y2={50} />
            <line x1={86} y1={50} x2={96} y2={50} />
          </g>

          {/* Bearing needle — points to a fixed N-by-NE azimuth. The
              north half is the accent token, the south half currentColor. */}
          <polygon points="50,50 45,54 50,14 55,54" fill="var(--color-accent)" opacity={0.9} />
          <polygon points="50,50 45,46 50,86 55,46" fill="currentColor" opacity={0.35} />
          <circle cx={50} cy={50} r={2.5} fill="var(--color-accent)" />
        </svg>
      </div>

      {annotations && <PlateAnnotations />}
    </div>
  );
}

// ── Coordinate annotations ──────────────────────────────────────────
// All tenant-neutral: house identity + abstract survey metadata. No
// country, no city, no partner name.

const monoTag: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.15em",
  color: "var(--color-fg-subtle)",
  lineHeight: 1.5,
};

function PlateAnnotations() {
  const meta: ReadonlyArray<{ caption: string; label: string }> = [
    { caption: "PLATE", label: "L30 · SURVEY" },
    { caption: "BELT", label: "30°N – 30°S" },
    { caption: "PROJ.", label: "WGS 84" },
  ];
  return (
    <>
      {/* Top-left meta strip. */}
      <div style={{ position: "absolute", top: 24, left: 24, display: "flex", gap: 32 }}>
        {meta.map((item) => (
          <div key={item.caption} style={monoTag}>
            <div style={{ color: "var(--color-brand)" }}>{item.caption}</div>
            <div>{item.label}</div>
          </div>
        ))}
      </div>

      {/* Bottom-right house tag. */}
      <div style={{ position: "absolute", bottom: 24, right: 24, textAlign: "right" }}>
        <div style={{ ...monoTag, color: "var(--color-accent-hover)" }}>Latitude 30</div>
        <div style={monoTag}>The 30° belt · 30°N – 30°S</div>
      </div>

      {/* Corner registration crosshairs. */}
      <RegistrationMarks />
    </>
  );
}

function RegistrationMarks() {
  const positions: React.CSSProperties[] = [
    { top: 12, left: 12 },
    { top: 12, right: 12 },
    { bottom: 12, left: 12 },
    { bottom: 12, right: 12 },
  ];
  return (
    <>
      {positions.map((pos, i) => (
        // eslint-disable-next-line l30/no-inline-svg-icon -- decorative registration crosshair, not iconography (canon 08-design/iconography.md exception)
        <svg
          key={i}
          width={12}
          height={12}
          role="presentation"
          style={{ position: "absolute", pointerEvents: "none", ...pos }}
        >
          <line
            x1={6}
            y1={0}
            x2={6}
            y2={12}
            stroke="var(--color-brand)"
            strokeWidth={0.5}
            opacity={0.6}
          />
          <line
            x1={0}
            y1={6}
            x2={12}
            y2={6}
            stroke="var(--color-brand)"
            strokeWidth={0.5}
            opacity={0.6}
          />
        </svg>
      ))}
    </>
  );
}
